import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { RootState, AppDispatch } from '../../store';
import { fetchTemplates } from '../../store/templatesSlice';
import { fetchCover } from '../../store/coverSlice';
import { fetchPhotos } from '../../store/photosSlice';
import { fetchAnswers } from '../../store/answersSlice';
import { TopNav } from '../../components/TopNav/TopNav';
import { BRAND, openExternal } from '../../brand';
import '../../components/BentoCard/BentoCard.css';
import {
  coverByValue,
  decodeCover,
  isCustomCover,
  customUrlFromId,
  defaultCover,
} from '../../components/BookCover';
import { BookCover } from '../../components/BookCover/BookCover';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { BookPage, PAGE_PRESETS, PagePreset, DEFAULT_PAGE_PRESET } from '../../components/BookPage';
import {
  ComabooksPhotoPage,
  ComabooksTextPage,
  decodeComabooksPhoto,
} from '../../components/ComabooksPage';
import { templatesApi, userChaptersApi, Question, Chapter, Photo } from '../../api';
import { getBookStrings } from '../../data/bookStrings';
import { CountryPicker } from '../../components/CountryPicker/CountryPicker';
import { Onboarding } from '../Onboarding/Onboarding';
import { ComabooksCover, isComabooksCoverId } from '../../components/ComabooksCover';
import { TemplatePicker } from '../Onboarding/TemplatePicker';
import leftIcon from '../../assets/left.svg';
import rightIcon from '../../assets/right.svg';
import chapterFrame from '../../assets/chapter-frame.png';
import '../../styles/margin.css';
import './Dashboard.css';
import BookProgress from '../../components/BookProgress/BookProgress';
import ProofReview from './ProofReview';

gsap.registerPlugin(ScrollTrigger);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const safeISO = (d: Date | null | undefined): string => {
  if (!d || isNaN(d.getTime())) return '';
  return d.toISOString();
};
const safeDateStr = (d: Date | null | undefined): string => {
  const iso = safeISO(d);
  return iso ? iso.split('T')[0] : '';
};

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface UserData {
  username: string;
  answerCount: number;
  questionCount: number;
  photoCount: number;
  completedOnboarding?: boolean;
  status?: 'inProcess' | 'done';
}

const makeFormatDeadline = (months: string[]) => (d: Date | null): string => {
  if (!d || isNaN(d.getTime()) || d.getTime() === 0) return '';
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const daysUntil = (d: Date | null): number | null => {
  if (!d || isNaN(d.getTime()) || d.getTime() === 0) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const diff = target.getTime() - now.getTime();
  return Math.round(diff / 86400000);
};

export const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const formatDeadline = makeFormatDeadline(
    t('months', { returnObjects: true }) as string[],
  );

  const templates = useSelector((state: RootState) => state.templates.templates);
  const templatesLoading = useSelector((state: RootState) => state.templates.loading);
  const cover = useSelector((state: RootState) => state.cover.value);
  const photos = useSelector((state: RootState) => state.photos.photos);
  const answers = useSelector((state: RootState) => state.answers.answers);

  const [userData, setUserData] = useState<UserData | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [deliveryCity, setDeliveryCity] = useState<string | null>(null);
  const [deliveryLoading, setDeliveryLoading] = useState(true);
  const [bookStatus, setBookStatus] = useState<string | null>(null);
  const [doneSpreadIndex, setDoneSpreadIndex] = useState<number>(0);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState<'finishBook' | 'confirmFinish' | 'noCover' | null>(null);

  const [street, setStreet] = useState<string>('');
  // «Пропустить редактуру»: книга сразу «Готово под печать» (backend services/skipEditing.ts).
  const [skipEditing, setSkipEditing] = useState(false);
  const [country, setCountry] = useState<string | null>(null);
  const [phone, setPhone] = useState<string>('');
  const [originalAddress, setOriginalAddress] = useState<string | null>(null);
  const [finishMinDate, setFinishMinDate] = useState<Date>(new Date());
  const deadlineRef = useRef<HTMLDivElement | null>(null);

  const [pdfUrl, setPdfUrl] = useState<string>();
  // Редактор прислал вёрстку на утверждение — её блок встаёт вместо «верстки».
  const [proofActive, setProofActive] = useState(false);
  const [progressKey, setProgressKey] = useState(0);
  const [numPages, setNumPages] = useState<number>();
  const [answersReady, setAnswersReady] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [feedFilter, setFeedFilter] = useState<
    'all' | 'layout' | 'answered' | 'unanswered' | 'photos'
  >('all');
  const [onboardingDoneLocal, setOnboardingDoneLocal] = useState<boolean>(false);
  const [feedView, setFeedView] = useState<'pages' | 'questions' | 'preview'>('pages');

  // ============== preview modal state (book-spread viewer) ==============
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [previewNumPages, setPreviewNumPages] = useState<number>(0);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  // spreadIndex === 0 → (blank, page 1) — first open-book view, page 1 on the right
  // spreadIndex === k → PDF pages (2k, 2k+1) shown left/right
  const [spreadIndex, setSpreadIndex] = useState<number>(0);

  const template = templates[0];
  const templateId = template?.id;
  const bookStrings = getBookStrings(template?.language);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setUserLoading(false);
          return;
        }
        const response = await axios.get(`${API_URL}/auth/summary`, {
          headers: { Authorization: `${token}` }
        });
        setUserData(response.data);
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      } finally {
        setUserLoading(false);
      }
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchDeliveryData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setDeliveryLoading(false);
        return;
      }
      try {
        const response = await fetch(`${API_URL}/contacts/my`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('No contacts');
        const data = await response.json();
        const { city, country, address, phone: phoneData, status } = data;

        setOriginalAddress(city);
        setStreet(address || '');
        setCountry(country || null);
        setPhone(phoneData || '');
        setBookStatus(status || null);

        if (city) {
          setDeliveryCity(city);
        }
      } catch {
        /* нет данных по контактам — оставляем пустыми */
      } finally {
        setDeliveryLoading(false);
      }
    };
    fetchDeliveryData();
  }, []);

  useEffect(() => {
    dispatch(fetchTemplates());
  }, [dispatch]);

  useEffect(() => {
    if (templateId) {
      dispatch(fetchCover(Number(templateId)));
      dispatch(fetchPhotos(Number(templateId)));
      templatesApi.getQuestions(Number(templateId)).then(({ data }) => setQuestions(data));
      userChaptersApi.list(Number(templateId)).then(({ data }) => setChapters(data));
    }
  }, [templateId, dispatch]);

  const totalQuestions = userData?.questionCount || 0;
  const answeredQuestions = userData?.answerCount || 0;

  const photosArray = Object.values(photos);

  const userName = userData?.username || '';

  const isBookDone = bookStatus === 'done';

  useEffect(() => {
    if (templateId) {
      setAnswersReady(false);
      dispatch(fetchAnswers(Number(templateId))).then(() => {
        setAnswersReady(true);
      });
    }
  }, [templateId, dispatch]);

  const generatePdf = useCallback(async () => {
    if (!isBookDone || !templateId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/pdf/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const blob = await response.blob();
      setPdfUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
    } catch (err) {
      console.error('failed to fetch book PDF', err);
    }
  }, [isBookDone, templateId]);

  useEffect(() => {
    if (isBookDone && templateId && answersReady) {
      generatePdf();
    }
  }, [isBookDone, templateId, answersReady, generatePdf]);

  // fetch the backend-generated full-book PDF when the user opens the
  // Preview tab (once per templateId / answers change)
  useEffect(() => {
    if (feedView !== 'preview' || !templateId) return;
    let canceled = false;
    const fetchPreview = async () => {
      setPreviewLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/pdf/${templateId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`preview ${response.status}`);
        const blob = await response.blob();
        if (canceled) return;
        setPreviewPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      } catch (err) {
        console.error('book-preview fetch failed', err);
      } finally {
        if (!canceled) setPreviewLoading(false);
      }
    };
    fetchPreview();
    return () => {
      canceled = true;
    };
  }, [feedView, templateId, answersReady]);

  // keyboard nav for the book preview
  useEffect(() => {
    if (feedView !== 'preview') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        setSpreadIndex((i) => {
          const maxSpread = Math.max(
            0,
            Math.ceil((previewNumPages + 1) / 2) - 1,
          );
          return Math.min(i + 1, maxSpread);
        });
      } else if (e.key === 'ArrowLeft') {
        setSpreadIndex((i) => Math.max(i - 1, 0));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [feedView, previewNumPages]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupType(null);
  };

  // Обложка обязательна до завершения: без названия книги показываем попап
  // «создайте обложку» и не пускаем к подтверждению.
  const fetchCoverReady = async (): Promise<boolean> => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
      const res = await fetch(`${API_URL}/covers/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return false;
      const cov = await res.json();
      return !!(cov?.bookTitle && String(cov.bookTitle).trim());
    } catch {
      return false;
    }
  };

  const handleFinishButtonClick = async () => {
    const ready = await fetchCoverReady();
    setPopupType(ready ? 'confirmFinish' : 'noCover');
    setShowPopup(true);
  };

  const handleConfirmFinishProceed = async () => {
    closePopup();
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response2 = await fetch(`${API_URL}/covers/${templateId}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response2.ok) {
        setPopupType('noCover');
        setShowPopup(true);
        return;
      }
      const response = await fetch(`${API_URL}/contacts/my`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const { city, country: countryData, address, phone: phoneData } = data;

      setOriginalAddress(city);
      setDeliveryCity(city);
      setStreet(address || '');
      setCountry(countryData || null);
      setPhone(phoneData || '');

      setPopupType('finishBook');
      setShowPopup(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleFinishBook = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const updatedData = {
        country,
        city: deliveryCity || originalAddress,
        address: street || '',
        phone: phone,
        status: 'done',
        skipEditing,
      };
      await fetch(`${API_URL}/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedData),
      });
      setShowPopup(false);
      setBookStatus('done');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const hasValidCity = !!(deliveryCity || originalAddress);
  const isFinishBookSaveDisabled = !country || !hasValidCity || !street || !phone;

  const coverReady = !!cover?.coverUrl && !!cover?.bookTitle;
  const photoCount = Object.keys(photos).length;

  const coverPaintingId = decodeCover(cover?.coverUrl);
  const coverIsCustom = isCustomCover(coverPaintingId);
  const coverEntry = coverIsCustom ? undefined : coverByValue(coverPaintingId);
  const coverBgUrl = coverIsCustom
    ? customUrlFromId(coverPaintingId)
    : coverEntry?.url ?? '';

  // ============== framer-style pinned scroll runway ==============
  // intro is a tall (200vh) container holding a sticky 100vh inner.
  // while sticky is pinned the user scrolls a "runway" — wheel/touch input
  // keeps the question on screen, scaling up and lifting away as --p climbs
  // 0 → 1 across the runway. only after the runway is consumed does the
  // sticky release and modules come into view. heaviness comes from the
  // length of the runway, not from damping the wheel.
  // GSAP ScrollTrigger: scrub-driven animation + directional snap.
  // - intro-content fades / scales / lifts over the natural intro height
  // - directional snap means: any forward scroll → snap to end (modules at top);
  //   any backward scroll → snap back to intro start.
  // Modules itself just lives in normal flow; snapping past intro reveals it
  // at the top of the viewport.

  const nextUnanswered = answeredQuestions + 1;
  const nothingStarted = answeredQuestions === 0;

  // ============== book feed (grid of page cells) ==============

  interface FeedCell {
    key: string;
    type:
      | 'question-answered'
      | 'question-empty'
      | 'photo'
      | 'static'
      | 'chapter';
    page: number;
    /** How many pages this cell spans. Photos and chapters are always 1; answered questions may span multiple. */
    pageCount: number;
    // question-specific
    questionId?: number;
    questionText?: string;
    answerText?: string;
    answerPreset?: PagePreset;
    // photo-specific
    photoId?: number;
    photoUrl?: string;
    photoDesc?: string;
    photoDate?: string;
    photoPosition?: string;
    photoShowDate?: boolean;
    photoShowDesc?: boolean;
    // static (front matter) specific
    staticKind?: 'title' | 'blank' | 'toc' | 'letter';
    staticLabel?: string;
    // chapter-specific
    chapterId?: number;
    chapterNumber?: number;
    chapterName?: string;
    chapterHidden?: boolean;
  }

  const cells: FeedCell[] = [];
  // Page rules (mirror backend pdfGenerator.ts exactly):
  //   - With chapters: 6 front-matter pages
  //       1 title / 2 blank / 3 TOC / 4 blank / 5 letter / 6 blank
  //   - Without chapters: 3 front-matter pages
  //       1 title / 2 blank / 3 letter — text starts on the left (page 4)
  //       (orig. blank+TOC pages 2-3 are dropped)
  //   - Each chapter inserts a 1-page chapter title BEFORE the first answered
  //     question of that chapter — counted in the page total. Chapters with
  //     no answered questions still appear in the grid as a placeholder
  //     (page=0) so the user sees the structure of the book.
  //   - Answered question takes ans.pageCount pages (fallback 1).
  //   - Each photo attached to a question is one page, right after that question.
  //   - Unanswered questions / chapters with no answered questions get page=0
  //     and do NOT advance the counter.
  const hasChapters = chapters.length > 0;
  const STATIC_CELLS: Array<{ kind: 'title' | 'blank' | 'toc' | 'letter'; label: string }> = hasChapters
    ? [
        { kind: 'title', label: 'титул' },
        { kind: 'blank', label: 'пусто' },
        { kind: 'toc', label: 'оглавление' },
        { kind: 'blank', label: 'пусто' },
        { kind: 'letter', label: 'письмо от издателя' },
        { kind: 'blank', label: 'пусто' },
      ]
    : [
        { kind: 'title', label: 'титул' },
        { kind: 'blank', label: 'пусто' },
        { kind: 'letter', label: 'письмо от издателя' },
      ];
  const FRONT_MATTER_PAGES = STATIC_CELLS.length;
  STATIC_CELLS.forEach((s, idx) => {
    cells.push({
      key: `static-${idx + 1}`,
      type: 'static',
      page: idx + 1,
      pageCount: 1,
      staticKind: s.kind,
      staticLabel: s.label,
    });
  });
  if (questions.length) {
    const photosArr = Object.values(photos);

    // Find chapter index for a given question sortOrder.
    const chapterFor = (sortOrder: number): number => {
      for (let i = 0; i < chapters.length; i += 1) {
        const c = chapters[i];
        if (sortOrder >= c.fromSort && sortOrder <= c.toSort) return i;
      }
      return -1;
    };

    // Pre-pass: для каждой главы — есть ли хоть один отвеченный вопрос?
    // Это решает, появится ли её title-страница в PDF, и соответственно
    // показывать ли ячейку как реальную страницу или плейсхолдер.
    const chapterHasAnswered = new Map<number, boolean>();
    for (const q of questions) {
      const ans = answers[q.id];
      if (!(ans && ans.text && ans.text.trim())) continue;
      const idx = chapterFor(q.sortOrder);
      if (idx !== -1) chapterHasAnswered.set(idx, true);
    }

    let page = FRONT_MATTER_PAGES + 1;
    let lastChapterShown = -1;
    // Sequential count of chapters that actually appear in the book — drives
    // the "Глава N" label so the grid matches the PDF (1, 2, 3, … rather than
    // the source template's gappy chapter indices).
    let displayedChapterCount = 0;
    for (const q of questions) {
      const ans = answers[q.id];
      const answered = !!(ans && ans.text && ans.text.trim());

      if (hasChapters) {
        const idx = chapterFor(q.sortOrder);
        if (idx !== -1 && idx !== lastChapterShown) {
          lastChapterShown = idx;
          const chap = chapters[idx];
          const isHidden = !!chap.isHidden;
          const hasAnswered = chapterHasAnswered.get(idx) ?? false;

          // Главу показываем в гриде только если внутри уже есть хоть один
          // ответ (как было раньше) — ИЛИ если юзер её явно скрыл, чтобы
          // оставить кликабельный плейсхолдер для возврата. Не доходим до
          // главы → её в гриде нет.
          if (hasAnswered || isHidden) {
            const willRender = hasAnswered && !isHidden;

            let chapterPage = 0;
            let chapterNumber = 0;
            if (willRender) {
              displayedChapterCount += 1;
              chapterPage = page;
              chapterNumber = displayedChapterCount;
              page += 1;
            }

            cells.push({
              key: `chapter-${chap.id}`,
              type: 'chapter',
              page: chapterPage,
              pageCount: 1,
              chapterId: chap.id,
              chapterNumber,
              chapterName: chap.name,
              chapterHidden: isHidden,
            });
          }
        }
      }

      const ansPreset = (PAGE_PRESETS.find((p) => p.id === ans?.preset)?.id
        ?? DEFAULT_PAGE_PRESET) as PagePreset;
      const qPageCount = answered ? ans?.pageCount ?? 1 : 1;
      const qPage = answered ? page : 0;
      cells.push({
        key: `q-${q.id}`,
        type: answered ? 'question-answered' : 'question-empty',
        page: qPage,
        pageCount: qPageCount,
        questionId: q.id,
        questionText: q.text,
        answerText: answered ? ans.text : '',
        answerPreset: ansPreset,
      });
      if (answered) page += qPageCount;
      const attached = photosArr.filter((p: Photo) => p.questionTxt === q.text);
      for (const p of attached) {
        const photoPage = answered ? page : 0;
        cells.push({
          key: `p-${p.id}`,
          type: 'photo',
          page: photoPage,
          pageCount: 1,
          photoId: p.id,
          photoUrl: p.url,
          photoDesc: p.description || '',
          // Только дата, проставленная юзером; createdAt (таймстемп загрузки)
          // в превью не подставляем — нет даты, нет подписи.
          photoDate: p.date || undefined,
          photoPosition: p.position,
          photoShowDate: p.showDate !== false,
          photoShowDesc: p.showDescription !== false,
        });
        if (answered) page += 1;
      }
    }
    const orphanedPhotos = photosArr.filter(
      (p: Photo) => !questions.some(q => q.text === p.questionTxt),
    );
    for (const p of orphanedPhotos) {
      cells.push({
        key: `p-${p.id}`,
        type: 'photo',
        page: 0,
        pageCount: 1,
        photoId: p.id,
        photoUrl: p.url,
        photoDesc: p.description || '',
        photoDate: p.date || undefined,
        photoPosition: p.position,
        photoShowDate: p.showDate !== false,
        photoShowDesc: p.showDescription !== false,
      });
    }
  }

  /** Real book length derived from the same cell layout shown in the grid —
   *  sum of (page + pageCount - 1) across cells with a real page number. */
  const pagesFilled = cells.reduce(
    (max, c) => (c.page > 0 ? Math.max(max, c.page + c.pageCount - 1) : max),
    FRONT_MATTER_PAGES,
  );

  /** Minimum filled pages before the book can be finished. */
  const MIN_PAGES_TO_FINISH = 60;

  /** 200 = aspirational full-book length. 150 = "feels whole". */
  const PAGE_TARGET = 200;
  const progressMilestoneKey = (n: number): string => {
    if (n === 0) return 'progress_milestone_start';
    if (n < 30) return 'progress_milestone_begun';
    if (n < 80) return 'progress_milestone_steady';
    if (n < 130) return 'progress_milestone_halfway';
    if (n < 150) return 'progress_milestone_almost';
    if (n < 200) return 'progress_milestone_full';
    return 'progress_milestone_bonus';
  };
  const progressPct = Math.min(100, Math.round((pagesFilled / PAGE_TARGET) * 100));

  const visibleCells = (() => {
    switch (feedFilter) {
      case 'all':
        return cells;
      case 'layout':
        // book layout: everything except unanswered questions
        return cells.filter((c) => c.type !== 'question-empty');
      case 'answered':
        return cells.filter((c) => c.type === 'question-answered');
      case 'unanswered':
        return cells.filter((c) => c.type === 'question-empty');
      case 'photos':
        return cells.filter((c) => c.type === 'photo');
    }
  })();

  const nextUnansweredQ = questions.find(q => {
    const a = answers[q.id];
    return !a || !a.text || !a.text.trim();
  });

  const pluralize = (n: number, one: string, few: string, many: string) => {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return `${n} ${one}`;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${n} ${few}`;
    return `${n} ${many}`;
  };

  const truncate = (s: string, n: number) =>
    s.length > n ? s.slice(0, n - 1).trimEnd() + '…' : s;

  const WORDS_PER_PAGE: Record<PagePreset, number> = {
    heading: 95,
    body: 210,
    balanced: 150,
  };

  const firstPageSlice = (text: string, preset: PagePreset): string => {
    const words = text.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return '';
    return words.slice(0, WORDS_PER_PAGE[preset]).join(' ');
  };

  const formatPhotoDate = (iso: string | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}.${d.getFullYear()}`;
  };

  // Step 0 of onboarding — pick a recipient + language → assign a template
  // to the user. Only kicks in after we've confirmed the user has zero
  // templates and hasn't completed onboarding yet.
  const needsTemplate =
    !templatesLoading &&
    templates.length === 0 &&
    !userLoading &&
    userData?.completedOnboarding === false;

  const needsOnboarding =
    !needsTemplate &&
    !onboardingDoneLocal &&
    !!templateId &&
    !userLoading &&
    userData?.completedOnboarding === false;

  return (
    <div className="mg-page">
      {needsTemplate && (
        <TemplatePicker
          onAssigned={() => dispatch(fetchTemplates())}
        />
      )}
      {needsOnboarding && templateId && (
        <Onboarding
          templateId={Number(templateId)}
          recipient={template?.recipient}
          onComplete={async () => {
            try {
              const token = localStorage.getItem('token');
              await fetch(`${API_URL}/auth/complete-onboarding`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
              });
            } catch (err) {
              console.error('failed to mark onboarding complete', err);
            }
            setOnboardingDoneLocal(true);
            if (templateId) dispatch(fetchCover(Number(templateId)));
          }}
        />
      )}
      <TopNav onLogout={handleLogout} />

      <main className="mg-main mg-dash">
        {/* ---------- user block ---------- */}
        <div className="cb-user">
          <div className={`cb-user__avatar ${userLoading ? 'v2-skeleton' : ''}`}>
            {!userLoading && <span>{(userName || BRAND.name).charAt(0).toUpperCase()}</span>}
          </div>
          <div className="cb-user__info">
            {userLoading ? (
              <>
                <div className="v2-skeleton cb-user__name-skeleton" />
                <div className="v2-skeleton cb-user__sub-skeleton" />
              </>
            ) : (
              <>
                <div className="cb-user__name">{userName || t('user_fallback')}</div>
                <div className="cb-user__sub">
                  {isBookDone ? t('rite_delivered') : t('rite_in_progress')}
                </div>
              </>
            )}
          </div>
          {!deliveryLoading && (
            isBookDone ? (
              <button className="cb-pill cb-user__finish" type="button" disabled>
                {t('finish_done')}
              </button>
            ) : (
              <button
                className="cb-pill cb-user__finish"
                onClick={handleFinishButtonClick}
                type="button"
                disabled={pagesFilled < MIN_PAGES_TO_FINISH}
                title={pagesFilled < MIN_PAGES_TO_FINISH ? t('finish_locked', { n: MIN_PAGES_TO_FINISH - pagesFilled }) as string : undefined}
              >
                {t('finish_button')}
              </button>
            )
          )}
        </div>

        {/* ---------- pages strip ---------- */}
        {!isBookDone && (
          <div className="cb-strip">
            <div className="cb-strip__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
              </svg>
            </div>
            <div className="cb-strip__content">
              <div className="cb-strip__count">
                {pagesFilled} {t('pages_written')}
              </div>
              <div className="cb-strip__text">
                {pagesFilled < MIN_PAGES_TO_FINISH
                  ? t('finish_locked', { n: MIN_PAGES_TO_FINISH - pagesFilled })
                  : t(progressMilestoneKey(pagesFilled))}
              </div>
              <div
                className="cb-strip__bar"
                role="progressbar"
                aria-valuenow={pagesFilled}
                aria-valuemin={0}
                aria-valuemax={PAGE_TARGET}
              >
                <div className="cb-strip__fill" style={{ width: `${progressPct}%` }} />
              </div>
            </div>
          </div>
        )}

        {/* ---------- bento grid ---------- */}
        <section className={`cb-bento ${isBookDone ? 'cb-bento--done' : ''}`}>
          <button
            className="v2-bento-card cb-bento__cover"
            onClick={() => !isBookDone && navigate(`/forms/cover/${templateId}`)}
            type="button"
            disabled={isBookDone}
          >
            <div className="cb-bento__cover-thumb">
              {isComabooksCoverId(cover?.coverUrl) || !coverBgUrl ? (
                <ComabooksCover
                  coverId={cover?.coverUrl}
                  title={cover?.bookTitle ?? ''}
                  author={cover?.authorName ?? ''}
                  receiver={cover?.receiverName ?? ''}
                />
              ) : (
                <BookCover
                  background={coverBgUrl}
                  title={cover?.bookTitle ?? ''}
                  author={cover?.authorName ?? ''}
                  platePreset={cover?.platePreset ?? 'frame'}
                  printPlate
                />
              )}
            </div>
            <div className="v2-bento-card__content">
              <div className="v2-bento-card__title">{t('mod_cover_eyebrow')}</div>
              <div className="v2-bento-card__subtitle cb-bento__ellipsis">
                {cover?.bookTitle ? cover.bookTitle : t('mod_cover_design')}
              </div>
            </div>
          </button>

          <button
            className="v2-bento-card cb-bento__answers"
            onClick={() =>
              !isBookDone &&
              navigate(
                nextUnansweredQ
                  ? `/forms/questions/${templateId}?q=${nextUnansweredQ.id}`
                  : `/forms/questions/${templateId}`,
              )
            }
            type="button"
            disabled={isBookDone}
          >
            <div className="v2-bento-card__header">
              <div className="v2-bento-card__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
              </div>
            </div>
            <div className="v2-bento-card__content">
              <div className="v2-bento-card__title">{t('mod_answers')}</div>
              {userLoading ? (
                <div className="v2-skeleton v2-bento-card__value-skeleton" />
              ) : (
                <div className="v2-bento-card__value">
                  {answeredQuestions} / {totalQuestions || questions.length}
                </div>
              )}
            </div>
          </button>

          {BRAND.instagramUrl ? (
            <button
              className="v2-bento-card cb-bento__insta"
              onClick={() => openExternal(BRAND.instagramUrl)}
              type="button"
            >
              <div className="v2-bento-card__header">
                <div className="v2-bento-card__icon">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                </div>
              </div>
              <div className="v2-bento-card__content">
                <div className="v2-bento-card__title">{BRAND.instagramHandle}</div>
                <div className="v2-bento-card__subtitle">Instagram</div>
              </div>
            </button>
          ) : (
            <div className="cb-bento__spacer" aria-hidden="true" />
          )}

          <button
            className="v2-bento-card v2-bento-card--wide cb-bento__photos"
            onClick={() => {
              if (isBookDone) return;
              setFeedView('pages');
              setFeedFilter('photos');
              // Wait for the grid to render with the new filter, then scroll to it.
              window.setTimeout(() => {
                const grid = document.querySelector('.mg-dash__feed');
                if (grid) {
                  grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 50);
            }}
            type="button"
            disabled={isBookDone}
          >
            <div className="v2-bento-card__photos">
              {photoCount > 0 ? (
                <div className={`cb-bento__photo-mosaic cb-bento__photo-mosaic--${Math.min(photoCount, 4)}`}>
                  {photosArray.slice(0, 4).map((p: Photo) => (
                    <div key={p.id} className="cb-bento__photo">
                      {p.url && <img src={p.url} alt="" loading="lazy" />}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="v2-bento-card__photos-empty">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                </div>
              )}
            </div>
            <div className="v2-bento-card__info">
              <div className="v2-bento-card__header">
                <div className="v2-bento-card__icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                </div>
              </div>
              <div className="v2-bento-card__content">
                <div className="v2-bento-card__title">{t('mod_photos_eyebrow')}</div>
                <div className="v2-bento-card__value">{photoCount}</div>
              </div>
            </div>
          </button>

          {BRAND.supportUrl ? (
            <button
              className="v2-bento-card cb-bento__support"
              onClick={() => openExternal(BRAND.supportUrl)}
              type="button"
            >
              <div className="v2-bento-card__header">
                <div className="v2-bento-card__icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                  </svg>
                </div>
              </div>
              <div className="v2-bento-card__content">
                <div className="v2-bento-card__title">{t('support_title')}</div>
                <div className="v2-bento-card__subtitle">{t('support_sub')}</div>
              </div>
            </button>
          ) : (
            <div className="cb-bento__spacer" aria-hidden="true" />
          )}
        </section>

        {!isBookDone && (
          <section className="mg-dash__feed">
            <div className="mg-dash__feed-head">
              <div className="mg-dash__tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={feedView === 'pages'}
                  className={`mg-dash__tab ${feedView === 'pages' ? 'mg-dash__tab--active' : ''}`}
                  onClick={() => setFeedView('pages')}
                >
                  {t('tab_pages')}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={feedView === 'questions'}
                  className={`mg-dash__tab ${feedView === 'questions' ? 'mg-dash__tab--active' : ''}`}
                  onClick={() => setFeedView('questions')}
                >
                  {t('tab_questions')}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={feedView === 'preview'}
                  className={`mg-dash__tab ${feedView === 'preview' ? 'mg-dash__tab--active' : ''}`}
                  onClick={() => setFeedView('preview')}
                >
                  {t('tab_preview')}
                </button>
              </div>
              {feedView === 'pages' && (
                <select
                  className="mg-dash__feed-filter"
                  value={feedFilter}
                  onChange={(e) =>
                    setFeedFilter(e.target.value as typeof feedFilter)
                  }
                >
                  <option value="all">{t('filter_all')}</option>
                  <option value="layout">{t('filter_layout')}</option>
                  <option value="answered">{t('filter_answered')}</option>
                  <option value="unanswered">{t('filter_unanswered')}</option>
                  <option value="photos">{t('filter_photos')}</option>
                </select>
              )}
            </div>

            {nextUnansweredQ && (
              <button
                className="mg-dash__composer-suggest"
                onClick={() =>
                  navigate(
                    `/forms/questions/${templateId}?q=${nextUnansweredQ.id}`,
                  )
                }
                type="button"
              >
                <span className="mg-dash__composer-roman">§</span>
                <span className="mg-dash__composer-q-wrap">
                  <span className="mg-dash__composer-meta">
                    {t('composer_next_q', { n: questions.indexOf(nextUnansweredQ) + 1 })}
                  </span>
                  <span className="mg-dash__composer-q">
                    {truncate(nextUnansweredQ.text, 140)}
                  </span>
                </span>
                <span className="mg-dash__composer-arrow">{t('composer_answer')}</span>
              </button>
            )}

            {feedView === 'pages' && (visibleCells.length > 0 ? (
              <div className="mg-dash__grid">
                {visibleCells.map((cell) => {
                  if (cell.type === 'static') {
                    return (
                      <div
                        key={cell.key}
                        className={`mg-dash__cell mg-dash__cell--static mg-dash__cell--static-${cell.staticKind}`}
                      >
                        <div className="mg-dash__cell-stack">
                          <div className="mg-dash__cell-thumb mg-dash__static-thumb">
                            {cell.staticKind === 'title' && (
                              <div className="mg-dash__static-title">
                                {cover?.authorName && (
                                  <div className="mg-dash__static-title-author">
                                    {cover.authorName}
                                  </div>
                                )}
                                {cover?.bookTitle ? (
                                  <div className="mg-dash__static-title-main">
                                    {cover.bookTitle}
                                  </div>
                                ) : (
                                  <div className="mg-dash__static-title-main mg-dash__static-title-main--placeholder">
                                    {bookStrings.titlePlaceholder}
                                  </div>
                                )}
                                {cover?.receiverName && (
                                  <div className="mg-dash__static-title-recipient">
                                    {bookStrings.receiverFor(cover.receiverName)}
                                  </div>
                                )}
                                <div className="mg-dash__static-title-imprint">{bookStrings.imprint} · 2026</div>
                              </div>
                            )}
                            {cell.staticKind === 'toc' && (
                              <div className="mg-dash__static-toc">
                                <div className="mg-dash__static-toc-label">{bookStrings.toc}</div>
                                <div className="mg-dash__static-toc-list">
                                  {cells
                                    .filter((c) => c.type === 'chapter' && !c.chapterHidden)
                                    .map((c) => (
                                      <div key={c.key} className="mg-dash__static-toc-row">
                                        <span className="mg-dash__static-toc-row-name">
                                          {bookStrings.chapterLabel(c.chapterNumber || 0)}: {c.chapterName}
                                        </span>
                                        <span className="mg-dash__static-toc-row-dots" aria-hidden="true" />
                                        <span className="mg-dash__static-toc-row-page">
                                          {c.page > 0 ? c.page : '—'}
                                        </span>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )}
                            {cell.staticKind === 'letter' && (
                              <div className="mg-dash__static-letter">
                                <div className="mg-dash__static-letter-stack">
                                  <div className="mg-dash__static-letter-title">{bookStrings.forewordPre}</div>
                                  <div className="mg-dash__static-letter-body">
                                    {bookStrings.forewordParagraphs.join('\n\n')}
                                  </div>
                                  <div className="mg-dash__static-letter-sig">{bookStrings.publisherSignature}</div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mg-dash__cell-meta">
                          <img
                            className="mg-dash__cell-side"
                            src={cell.page % 2 === 1 ? rightIcon : leftIcon}
                            alt={cell.page % 2 === 1 ? 'правая' : 'левая'}
                          />
                          <span className="mg-dash__cell-type">
                            Стр. {cell.page}
                          </span>
                        </div>
                      </div>
                    );
                  }
                  if (cell.type === 'chapter') {
                    const hidden = !!cell.chapterHidden;
                    return (
                      <button
                        key={cell.key}
                        className={`mg-dash__cell mg-dash__cell--chapter${
                          hidden ? ' mg-dash__cell--chapter--hidden' : ''
                        }`}
                        onClick={() =>
                          cell.chapterId &&
                          navigate(`/forms/chapter/${templateId}/${cell.chapterId}`)
                        }
                        type="button"
                      >
                        <div className="mg-dash__cell-stack">
                          <div className="mg-dash__cell-thumb mg-dash__chapter-thumb">
                            <img
                              className="mg-dash__chapter-frame mg-dash__chapter-frame--top"
                              src={chapterFrame}
                              alt=""
                              aria-hidden="true"
                            />
                            <div className="mg-dash__chapter-title">
                              {cell.chapterNumber
                                ? `${bookStrings.chapterLabel(cell.chapterNumber)}: ${cell.chapterName}`
                                : cell.chapterName}
                            </div>
                            <img
                              className="mg-dash__chapter-frame mg-dash__chapter-frame--bottom"
                              src={chapterFrame}
                              alt=""
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                        <div className="mg-dash__cell-meta">
                          {cell.page > 0 && (
                            <img
                              className="mg-dash__cell-side"
                              src={cell.page % 2 === 1 ? rightIcon : leftIcon}
                              alt={cell.page % 2 === 1 ? 'правая' : 'левая'}
                            />
                          )}
                          <span className="mg-dash__cell-type">
                            {hidden
                              ? t('chapter_cell_hidden')
                              : cell.page > 0
                                ? `Стр. ${cell.page}`
                                : '—'}
                          </span>
                        </div>
                      </button>
                    );
                  }
                  if (cell.type === 'photo') {
                    return (
                      <button
                        key={cell.key}
                        className="mg-dash__cell mg-dash__cell--photo"
                        onClick={() => navigate(`/forms/photos/${templateId}/${cell.photoId}`)}
                        type="button"
                      >
                        <div className="mg-dash__cell-stack">
                          <ComabooksPhotoPage
                            className="mg-dash__cell-thumb"
                            status={decodeComabooksPhoto(cell.photoPosition)}
                            photoUrl={cell.photoUrl || ''}
                            date={formatPhotoDate(cell.photoDate)}
                            description={cell.photoDesc || ''}
                            showDate={!!cell.photoShowDate}
                            showDescription={!!cell.photoShowDesc}
                            footerTitle={cover?.bookTitle || ''}
                            footerAuthor={cover?.authorName || ''}
                            pageNumber={cell.page}
                          />
                        </div>
                        <div className="mg-dash__cell-meta">
                          {cell.page > 0 && (
                            <img
                              className="mg-dash__cell-side"
                              src={cell.page % 2 === 1 ? rightIcon : leftIcon}
                              alt={cell.page % 2 === 1 ? 'правая' : 'левая'}
                            />
                          )}
                          <span className="mg-dash__cell-type">
                            {cell.page > 0
                              ? cell.pageCount > 1
                                ? `Стр. ${cell.page}-${cell.page + cell.pageCount - 1}`
                                : `Стр. ${cell.page}`
                              : '—'}
                          </span>
                        </div>
                      </button>
                    );
                  }
                  const empty = cell.type === 'question-empty';
                  return (
                    <button
                      key={cell.key}
                      className={`mg-dash__cell mg-dash__cell--q ${empty ? 'mg-dash__cell--empty' : ''}`}
                      onClick={() =>
                        navigate(
                          `/forms/questions/${templateId}?q=${cell.questionId}`,
                        )
                      }
                      type="button"
                    >
                      <div className="mg-dash__cell-stack">
                        <ComabooksTextPage
                          className="mg-dash__cell-thumb"
                          question={cell.questionText || ''}
                          answer={empty ? '' : cell.answerText || ''}
                          footerTitle={cover?.bookTitle || ''}
                          footerAuthor={cover?.authorName || ''}
                          pageNumber={cell.page}
                        />
                        {empty && (
                          <div className="mg-dash__cell-overlay">
                            <span className="mg-dash__cell-overlay-label">
                              {t('cell_answer_cta')}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="mg-dash__cell-meta">
                        {cell.page > 0 && (
                          <img
                            className="mg-dash__cell-side"
                            src={cell.page % 2 === 1 ? rightIcon : leftIcon}
                            alt={cell.page % 2 === 1 ? 'правая' : 'левая'}
                          />
                        )}
                        <span className="mg-dash__cell-type">
                          {cell.page > 0
                            ? cell.pageCount > 1
                              ? `Стр. ${cell.page}-${cell.page + cell.pageCount - 1}`
                              : `Стр. ${cell.page}`
                            : '—'}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="mg-dash__feed-empty">
                {t('feed_empty_state')}
              </div>
            ))}

            {feedView === 'questions' && (
              <div className="mg-dash__qdrawer mg-q__drawer mg-q__drawer--open">
                <div className="mg-q__qlist">
                  {questions.length > 0 ? (
                    <ol className="mg-q__qgroup-list">
                      {questions.map((q, idx) => {
                        const ans = answers[q.id];
                        const done = !!(ans && ans.text && ans.text.trim());
                        return (
                          <li
                            key={q.id}
                            className={`mg-q__qitem ${done ? 'mg-q__qitem--done' : ''}`}
                            onClick={() =>
                              navigate(`/forms/questions/${templateId}?q=${q.id}`)
                            }
                          >
                            <span className="mg-q__qitem-num">
                              {String(idx + 1).padStart(2, '0')}
                            </span>
                            <span className="mg-q__qitem-text">{q.text}</span>
                          </li>
                        );
                      })}
                    </ol>
                  ) : (
                    <div className="mg-dash__feed-empty">
                      {t('feed_empty_state')}
                    </div>
                  )}
                </div>
              </div>
            )}

            {feedView === 'preview' && (() => {
              // spread layout: spreadIndex 0 → (blank, p1), index k → (p2k, p2k+1).
              const leftPage = spreadIndex * 2;
              const rightPage = spreadIndex * 2 + 1;
              const maxSpread = Math.max(0, Math.ceil((previewNumPages + 1) / 2) - 1);
              const leftVisible = leftPage >= 1 && leftPage <= previewNumPages;
              const rightVisible = rightPage >= 1 && rightPage <= previewNumPages;
              const onPrev = () => spreadIndex > 0 && setSpreadIndex(spreadIndex - 1);
              const onNext = () => spreadIndex < maxSpread && setSpreadIndex(spreadIndex + 1);
              const indicator =
                leftVisible && rightVisible
                  ? `${leftPage}–${rightPage}`
                  : rightVisible
                    ? `${rightPage}`
                    : leftVisible
                      ? `${leftPage}`
                      : '—';
              return (
                <div className="mg-dash__preview">
                  {previewPdfUrl ? (
                    <Document
                      file={previewPdfUrl}
                      onLoadSuccess={({ numPages }) => setPreviewNumPages(numPages)}
                      loading={
                        <div className="mg-dash__preview-loading mg-meta">
                          {t('pdf_loading')}
                        </div>
                      }
                      className="mg-dash__preview-doc"
                    >
                      <div className="mg-dash__preview-spread">
                        <div className="mg-dash__preview-page">
                          {leftVisible ? (
                            <Page
                              key={`l-${leftPage}`}
                              pageNumber={leftPage}
                              width={380}
                              renderAnnotationLayer={false}
                              renderTextLayer={false}
                              loading={<div className="mg-dash__preview-blank" />}
                            />
                          ) : (
                            <div className="mg-dash__preview-blank" />
                          )}
                        </div>
                        <div className="mg-dash__preview-page">
                          {rightVisible ? (
                            <Page
                              key={`r-${rightPage}`}
                              pageNumber={rightPage}
                              width={380}
                              renderAnnotationLayer={false}
                              renderTextLayer={false}
                              loading={<div className="mg-dash__preview-blank" />}
                            />
                          ) : (
                            <div className="mg-dash__preview-blank" />
                          )}
                        </div>
                      </div>
                    </Document>
                  ) : (
                    <div className="mg-dash__preview-loading mg-meta">
                      {previewLoading ? t('pdf_loading') : '—'}
                    </div>
                  )}

                  <div className="mg-dash__preview-controls">
                    <button
                      type="button"
                      className="mg-dash__preview-nav"
                      onClick={onPrev}
                      disabled={spreadIndex === 0}
                      aria-label={t('preview_prev_aria') as string}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M15 6l-6 6 6 6" />
                      </svg>
                    </button>
                    <span className="mg-dash__preview-indicator">
                      {indicator}
                      {previewNumPages > 0 && (
                        <span className="mg-dash__preview-indicator-total"> / {previewNumPages}</span>
                      )}
                    </span>
                    <button
                      type="button"
                      className="mg-dash__preview-nav"
                      onClick={onNext}
                      disabled={spreadIndex >= maxSpread}
                      aria-label={t('preview_next_aria') as string}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M9 6l6 6-6 6" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })()}

          </section>
        )}

        {isBookDone && <BookProgress key={progressKey} />}

        <ProofReview onActive={setProofActive} onApproved={() => setProgressKey((k) => k + 1)} />

        {isBookDone && pdfUrl && !proofActive && (() => {
          const total = numPages ?? 0;
          const leftPage = doneSpreadIndex * 2;
          const rightPage = doneSpreadIndex * 2 + 1;
          const maxSpread = Math.max(0, Math.ceil((total + 1) / 2) - 1);
          const leftVisible = leftPage >= 1 && leftPage <= total;
          const rightVisible = rightPage >= 1 && rightPage <= total;
          const onPrev = () => doneSpreadIndex > 0 && setDoneSpreadIndex(doneSpreadIndex - 1);
          const onNext = () => doneSpreadIndex < maxSpread && setDoneSpreadIndex(doneSpreadIndex + 1);
          const indicator =
            leftVisible && rightVisible
              ? `${leftPage}–${rightPage}`
              : rightVisible
                ? `${rightPage}`
                : leftVisible
                  ? `${leftPage}`
                  : '—';
          return (
            <section className="mg-section mg-dash__pages-section">
              <div className="mg-label">{t('layout_label')}</div>
              <div className="mg-dash__preview">
                <Document
                  file={pdfUrl}
                  onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                  loading={
                    <div className="mg-dash__preview-loading mg-meta">
                      {t('pdf_loading')}
                    </div>
                  }
                  className="mg-dash__preview-doc"
                >
                  <div className="mg-dash__preview-spread">
                    <div className="mg-dash__preview-page">
                      {leftVisible ? (
                        <Page
                          key={`done-l-${leftPage}`}
                          pageNumber={leftPage}
                          width={380}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                          loading={<div className="mg-dash__preview-blank" />}
                        />
                      ) : (
                        <div className="mg-dash__preview-blank" />
                      )}
                    </div>
                    <div className="mg-dash__preview-page">
                      {rightVisible ? (
                        <Page
                          key={`done-r-${rightPage}`}
                          pageNumber={rightPage}
                          width={380}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                          loading={<div className="mg-dash__preview-blank" />}
                        />
                      ) : (
                        <div className="mg-dash__preview-blank" />
                      )}
                    </div>
                  </div>
                </Document>

                <div className="mg-dash__preview-controls">
                  <button
                    type="button"
                    className="mg-dash__preview-nav"
                    onClick={onPrev}
                    disabled={doneSpreadIndex === 0}
                    aria-label={t('preview_prev_aria') as string}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M15 6l-6 6 6 6" />
                    </svg>
                  </button>
                  <span className="mg-dash__preview-indicator">
                    {indicator}
                    {total > 0 && (
                      <span className="mg-dash__preview-indicator-total"> / {total}</span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="mg-dash__preview-nav"
                    onClick={onNext}
                    disabled={doneSpreadIndex >= maxSpread}
                    aria-label={t('preview_next_aria') as string}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </button>
                </div>
              </div>
            </section>
          );
        })()}

      </main>

      {showPopup && popupType === 'noCover' && (
        <div className="mg-popup-overlay" onClick={closePopup}>
          <div className="mg-popup mg-popup--rite" onClick={e => e.stopPropagation()}>
            
            <div className="mg-popup__title">{t('no_cover_title')}</div>
            <p className="mg-popup__text">{t('no_cover_text')}</p>
            <div className="mg-popup__actions">
              <button
                className="mg-btn mg-btn--accent"
                onClick={() => navigate(`/forms/cover/${templateId}`)}
              >
                {t('no_cover_btn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPopup && popupType === 'confirmFinish' && (
        <div className="mg-popup-overlay" onClick={closePopup}>
          <div className="mg-popup mg-popup--rite" onClick={e => e.stopPropagation()}>
            <div className="mg-rite">
              <span>{t('finish_rite_chapter')}</span>
              <span className="mg-rite__sep">·</span>
              <span className="mg-rite__now">{t('finish_rite_now')}</span>
            </div>
            <div className="mg-popup__title">{t('finish_title')}</div>
            <p className="mg-popup__text">{t('finish_text')}</p>
            <div className="mg-popup__actions">
              <button className="mg-btn mg-btn--accent" onClick={handleConfirmFinishProceed}>
                {t('finish_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPopup && popupType === 'finishBook' && (
        <div className="mg-popup-overlay" onClick={closePopup}>
          <div className="mg-popup mg-popup--rite" onClick={e => e.stopPropagation()}>
            <div className="mg-rite">
              <span>{t('finish_rite_chapter')}</span>
              <span className="mg-rite__sep">·</span>
              <span className="mg-rite__now">{t('send_rite_now')}</span>
            </div>
            <div className="mg-popup__title">
              {!originalAddress ? t('send_title_fill') : t('send_title_send')}
            </div>
            <div className="mg-field">
              <label className="mg-field__label">{t('send_field_country')}</label>
              <CountryPicker
                value={country}
                onChange={setCountry}
                placeholder={t('send_country_placeholder') as string}
              />
            </div>
            <div className="mg-field">
              <label className="mg-field__label">{t('send_field_city')}</label>
              <input
                type="text"
                className="mg-input"
                value={deliveryCity || ''}
                onChange={e => setDeliveryCity(e.target.value)}
              />
            </div>
            <div className="mg-field">
              <label className="mg-field__label">{t('send_field_street')}</label>
              <input
                type="text"
                className="mg-input"
                value={street}
                onChange={e => setStreet(e.target.value)}
              />
            </div>
            <div className="mg-field">
              <label className="mg-field__label">{t('send_field_phone')}</label>
              <input
                type="text"
                className="mg-input"
                value={phone}
                onChange={e => setPhone(e.target.value)}
              />
            </div>
            <label className="mg-checkbox mg-skip-editing">
              <input
                type="checkbox"
                checked={skipEditing}
                onChange={e => setSkipEditing(e.target.checked)}
              />
              <span>{t('send_skip_editing')}</span>
            </label>
            <div className="mg-popup__actions">
              <button
                className="mg-btn mg-btn--accent"
                disabled={isFinishBookSaveDisabled}
                onClick={handleFinishBook}
              >
                {t('send_submit')}
              </button>
              <button className="mg-btn mg-btn--ghost" onClick={closePopup}>
                {t('send_cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
